import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
  ArrowRight,
  Check,
  Mail,
  ShieldCheck,
  MapPin,
  LayoutDashboard,
  Wallet,
  MessageCircle,
  Star,
} from "lucide-react";
import { toast } from "sonner";
import { MaskedLines } from "./MaskedText";
import Marquee from "./Marquee";
import { api, extractErrorMessage } from "@/lib/api";

const HERO_IMG =
  "https://images.pexels.com/photos/5780833/pexels-photo-5780833.jpeg?auto=compress&cs=tinysrgb&w=1200";
const HERO_ACCENT_IMG =
  "https://images.unsplash.com/photo-1468531390554-9f62f9767a87?auto=format&fit=crop&w=800&q=80";

const CHECKLIST = [
  "Founding Vendor Status — lifetime priority visibility",
  "Your brand gets the spotlight, not lost in a generic marketplace",
  "0% commission* on your first 30 sales",
  "Dedicated brand promotion across digital & festival campaigns",
  "Just 7–9% commission thereafter, tiered by category — no flat cut, no hidden fees",
  "Reach 27+ million customers seeking authentic Bengali products across India",
  "Exclusive category advantage for early partners",
  "Grow with India's first dedicated marketplace for authentic Bengali brands",
];

const TRUST_INDICATORS = [
  { Icon: MapPin, label: "Indian Marketplace", note: "Made in India · Bengal-first" },
  { Icon: ShieldCheck, label: "Secure Vendor Registration", note: "FSSAI + GST compliant" },
  { Icon: LayoutDashboard, label: "Dedicated Seller Dashboard", note: "Live orders + payouts" },
  { Icon: Wallet, label: "Regular Weekly Payouts", note: "Transparent settlement" },
  { Icon: MessageCircle, label: "WhatsApp Support", note: "Real humans, fast replies" },
  { Icon: Star, label: "Trusted Bengal Marketplace", note: "Curated founding vendors" },
];

export default function Slide1Welcome({ onNext, onEmailSaved, existingEmail }) {
  const [email, setEmail] = useState(existingEmail || "");
  const [savingProgress, setSavingProgress] = useState(false);
  const heroRef = useRef(null);

  // Parallax
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 60, damping: 20, mass: 0.6 });
  const smy = useSpring(my, { stiffness: 60, damping: 20, mass: 0.6 });
  const heroX = useTransform(smx, [-1, 1], [-14, 14]);
  const heroY = useTransform(smy, [-1, 1], [-10, 10]);
  const accentX = useTransform(smx, [-1, 1], [22, -22]);
  const accentY = useTransform(smy, [-1, 1], [16, -16]);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      mx.set(x);
      my.set(y);
    };
    const onLeave = () => {
      mx.set(0);
      my.set(0);
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [mx, my]);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSaveProgress = async () => {
    if (!validEmail) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSavingProgress(true);
    try {
      const { data } = await api.post("/onboarding/save-progress", {
        email: email.trim(),
      });
      onEmailSaved?.(email.trim(), data.resume_token);
      toast.success("Resume link sent — check your inbox.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSavingProgress(false);
    }
  };

  const handleStart = () => {
    if (email && validEmail) {
      // fire and forget — save silently so they can resume if they leave later
      api
        .post("/onboarding/save-progress", { email: email.trim() })
        .then(({ data }) => onEmailSaved?.(email.trim(), data.resume_token))
        .catch(() => {});
    }
    onNext();
  };

  return (
    <div
      className="w-full grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-start"
      data-testid="slide-1-welcome"
    >
      {/* LEFT — editorial copy */}
      <div className="flex flex-col">
        <motion.div
          className="eyebrow"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
        >
          Vendor Onboarding · Chapter 01
        </motion.div>

        <h1 className="mt-4 md:mt-6 font-editorial text-[42px] sm:text-[56px] md:text-[68px] lg:text-[76px] leading-[1.02] tracking-[-0.01em] text-[color:var(--sbh-ink)]">
          <MaskedLines
            lines={["Become a", <span key="fv" className="italic text-[color:var(--sbh-terracotta)]">Founding Vendor</span>, "on Sonar Bangla Haat."]}
            delay={0.2}
            stagger={0.16}
          />
        </h1>

        <motion.p
          className="mt-6 md:mt-8 max-w-xl text-[15px] md:text-[17px] leading-relaxed text-[color:var(--sbh-ink-2)]"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
        >
          India&apos;s first dedicated marketplace for authentic Bengali brands. A quiet home for the loudest heritage in the country — built for makers, not middlemen.
        </motion.p>

        <motion.ul
          className="mt-8 md:mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl"
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.06, delayChildren: 1.0 } },
          }}
          data-testid="welcome-checklist"
        >
          {CHECKLIST.map((line, i) => (
            <motion.li
              key={i}
              className="flex items-start gap-3 text-[14px] leading-relaxed text-[color:var(--sbh-ink-2)]"
              variants={{
                hidden: { opacity: 0, y: 10 },
                show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
              }}
            >
              <span className="mt-[3px] shrink-0 w-4 h-4 border border-[color:var(--sbh-terracotta)] flex items-center justify-center">
                <Check className="w-3 h-3 text-[color:var(--sbh-terracotta)]" strokeWidth={2.4} />
              </span>
              <span>{line}</span>
            </motion.li>
          ))}
        </motion.ul>

        <motion.div
          className="mt-10 md:mt-14 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.6 }}
        >
          <button
            className="btn-editorial"
            onClick={handleStart}
            data-testid="welcome-start-btn"
          >
            Start Onboarding
            <ArrowRight className="arrow w-4 h-4" strokeWidth={2} />
          </button>
          <div className="text-[12px] tracking-[0.16em] uppercase text-[color:var(--sbh-muted)]">
            Takes 5 minutes · No commitment
          </div>
        </motion.div>

        <motion.div
          className="mt-10 md:mt-14 max-w-md"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.9 }}
        >
          <div className="eyebrow mb-3 flex items-center gap-2">
            <Mail className="w-3 h-3" strokeWidth={2.2} /> Save & Resume via Email
          </div>
          <div className="flex items-stretch border-b border-[color:var(--sbh-border)] focus-within:border-[color:var(--sbh-terracotta)] transition-colors">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="flex-1 bg-transparent outline-none py-3 text-[15px] font-body text-[color:var(--sbh-ink)] placeholder:text-[color:var(--sbh-muted)]"
              data-testid="welcome-email-input"
            />
            <button
              className="btn-ghost"
              onClick={handleSaveProgress}
              disabled={savingProgress}
              data-testid="welcome-save-progress-btn"
            >
              {savingProgress ? "Sending…" : "Send Link"}
            </button>
          </div>
          <p className="mt-2 text-[12px] text-[color:var(--sbh-muted)] leading-relaxed">
            We&apos;ll email you a private link so you can pick up exactly where you left off.
          </p>
        </motion.div>
      </div>

      {/* RIGHT — parallax hero */}
      <div
        ref={heroRef}
        className="relative w-full h-[380px] sm:h-[460px] lg:h-[620px]"
        data-testid="welcome-hero-visual"
      >
        {/* Editorial frame lines */}
        <div className="absolute inset-0 border border-[color:var(--sbh-border)] pointer-events-none" />
        <div className="absolute top-6 left-6 right-6 bottom-6 border-t border-b border-[color:var(--sbh-mustard)]/40 pointer-events-none" />

        {/* Main clipped hero image */}
        <motion.div
          className="absolute inset-6 overflow-hidden"
          style={{ x: heroX, y: heroY }}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
        >
          <img
            src={HERO_IMG}
            alt="Handcrafted terracotta from Bengal"
            className="w-full h-full object-cover"
            draggable="false"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--sbh-deep-red)]/25 via-transparent to-transparent" />
          <div className="grain opacity-25" />
        </motion.div>

        {/* Accent clipping — small editorial insert */}
        <motion.div
          className="hidden md:block absolute -bottom-6 -left-6 w-[46%] h-[38%] shadow-[0_20px_60px_-20px_rgba(59,23,19,0.35)] border border-[color:var(--sbh-border)] bg-[color:var(--sbh-cream)] p-2"
          style={{ x: accentX, y: accentY }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative w-full h-full overflow-hidden">
            <img
              src={HERO_ACCENT_IMG}
              alt="Stacked clay pots — editorial insert"
              className="w-full h-full object-cover"
              draggable="false"
            />
          </div>
          <div className="absolute -bottom-3 left-3 font-editorial italic text-[13px] tracking-wide text-[color:var(--sbh-ink-2)] bg-[color:var(--sbh-cream)] px-2">
            Fig. 01 — Mrittika, West Bengal
          </div>
        </motion.div>

        {/* Fig label for main */}
        <motion.div
          className="absolute -top-4 right-4 md:right-8 font-editorial italic text-[13px] tracking-wide text-[color:var(--sbh-ink-2)] bg-[color:var(--sbh-cream)] px-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.4 }}
        >
          Vol. I — Founding Vendors
        </motion.div>
      </div>

      {/* Trust Indicators — spans both columns, editorial grid */}
      <div
        className="lg:col-span-2 mt-14 md:mt-20"
        data-testid="trust-indicators"
      >
        <div className="eyebrow mb-6">Why sell on Sonar Bangla Haat</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
          {TRUST_INDICATORS.map((t, i) => (
            <motion.div
              key={t.label}
              className="border-t border-[color:var(--sbh-border)] pt-4 pr-4"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{
                duration: 0.6,
                delay: 0.05 * i,
                ease: [0.22, 1, 0.36, 1],
              }}
              data-testid={`trust-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <t.Icon
                className="w-5 h-5 text-[color:var(--sbh-terracotta)]"
                strokeWidth={1.6}
              />
              <div className="mt-3 font-editorial text-[16px] leading-tight text-[color:var(--sbh-ink)]">
                {t.label}
              </div>
              <div className="mt-1 text-[11.5px] tracking-[0.04em] text-[color:var(--sbh-muted)] leading-snug">
                {t.note}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Editorial testimonial / social-proof pull-quote */}
      <motion.aside
        className="lg:col-span-2 mt-14 md:mt-16 border-y border-[color:var(--sbh-border)] py-8 md:py-10 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-baseline"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        data-testid="slide1-testimonial"
      >
        <div className="eyebrow shrink-0">Founding Vendor Program</div>
        <blockquote className="font-editorial text-[24px] md:text-[30px] lg:text-[34px] leading-[1.2] tracking-[-0.005em] text-[color:var(--sbh-ink)]">
          &ldquo;Already onboarding brands across West Bengal — from
          <span className="italic text-[color:var(--sbh-terracotta)]"> nolen gur </span>
          in Bardhaman to
          <span className="italic text-[color:var(--sbh-terracotta)]"> terracotta </span>
          in Bishnupur. Your place at the founding table is waiting.&rdquo;
          <div className="mt-4 text-[12px] tracking-[0.22em] uppercase text-[color:var(--sbh-muted)] font-body not-italic">
            — Sonar Bangla Haat, curation team
          </div>
        </blockquote>
      </motion.aside>

      {/* Full-bleed marquee across the bottom of slide 1 */}
      <div className="lg:col-span-2 mt-2 md:mt-6 py-4 border-y border-[color:var(--sbh-border)]">
        <Marquee />
      </div>
    </div>
  );
}
